import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  SimpleGrid,
  Grid,
  VStack,
  HStack,
  Icon,
  Card,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
  useToken,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaDollarSign, FaUser, FaSync, FaHandshake } from 'react-icons/fa';
import { MdOutlineRocketLaunch, MdOutlineTrackChanges } from 'react-icons/md';
import { HiOutlineChartBar } from 'react-icons/hi2';
import { useAuth } from 'contexts/AuthContext';
import { useAnalytics } from 'hooks/useAnalytics';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getBaseUrl } from 'utils/url';
import axios from 'axios';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';
import { stripeService } from 'services/stripeService';
import { StripeSubscription, BackendSubscription } from 'types/stripe';
import { NavLink } from 'react-router-dom';
import { useNotifications } from 'hooks/useNotifications';
import { Notification } from 'types/notifications';
import { formatAbsoluteTime } from 'utils/timestamp';
import { EcosystemPlaybookCard } from './components/EcosystemPlaybookCard';

/** Shared height for Home Notifications + Get Started row (two-column layout) */
const HOME_NOTIFICATIONS_GET_STARTED_HEIGHT = '450px';

type PartnerListKey =
  | 'expansionUpsellPlay'
  | 'accelerateOps'
  | 'findNewOps'
  | 'buildPartnerships';

type PartnerListsResponse = {
  urls?: Partial<Record<PartnerListKey, string>>;
  counts?: Partial<Record<PartnerListKey, number | null>>;
};

export const Home = () => {
  const { user, token } = useAuth();
  const {
    currentMonthData,
    isLoading,
    error,
    fetchCurrentMonthAnalytics,
    triggerAnalytics,
    formatCurrency,
  } = useAnalytics();

  // Notifications hook
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    markAsRead,
    markAllAsRead,
  } = useNotifications(token);

  // Helper functions to calculate metrics from the new structure
  // const getClosedWonAmount = (data: any) => {
  //   return data?.closedwon?.total || 0;
  // };

  const getOpportunityValue = (data: any) => {
    // Sum all deal stages except closedwon
    const opportunity = data?.opportunity?.total || 0;
    const proposal = data?.proposal?.total || 0;
    const other = data?.other?.total || 0;
    // Add any other deal stages here as they are added to the API
    return opportunity + proposal + other;
  };

  // const getClosedWonCount = (data: any) => {
  //   return data?.closedwon?.deals?.length || 0;
  // };

  const getOpportunityCount = (data: any) => {
    const opportunity = data?.opportunity?.deals?.length || 0;
    const proposal = data?.proposal?.deals?.length || 0;
    const other = data?.other?.deals?.length || 0;
    return opportunity + proposal + other;
  };

  const [partnerLists, setPartnerLists] = useState<PartnerListsResponse>({});
  const [subscriptions, setSubscriptions] = useState<
    StripeSubscription | BackendSubscription | null
  >(null);

  const [onboardingStatus, setOnboardingStatus] = useLocalStorage(
    'onboardingStatus',
    {
      completeCompanyProfile: false,
      completeICP: false,
      runEcosystemSignals: false,
      connectHubSpot: false,
      requestFirstPartner: false,
    },
  );

  const [onboardingProgressFill] = useToken('colors', ['green.600']);

  // Helper function to handle checkbox toggle
  const handleOnboardingToggle = (step: keyof typeof onboardingStatus) => {
    const newStatus = !onboardingStatus[step];
    setOnboardingStatus((prev) => ({
      ...prev,
      [step]: newStatus,
    }));

    // Track onboarding step completion
    FirebaseAnalyticsService.trackOnboardingStep(
      step,
      getCurrentOnboardingStepNumber() + (newStatus ? 1 : 0),
      newStatus,
    );
  };

  // Helper function to get current onboarding step number
  const getCurrentOnboardingStepNumber = () => {
    const completed = [
      onboardingStatus.completeCompanyProfile,
      onboardingStatus.completeICP,
      Boolean(onboardingStatus.runEcosystemSignals),
      onboardingStatus.connectHubSpot,
      onboardingStatus.requestFirstPartner,
    ].filter(Boolean).length;
    // Return the number of completed steps (0-5)
    return completed;
  };

  // Helper function to check if analytics job is more than 1 hour old
  const isAnalyticsJobOldEnough = () => {
    if (!currentMonthData?.jobStatus?.lastRun?.completedAt) {
      return true; // If no job status, allow refresh
    }

    const completedAt = currentMonthData.jobStatus.lastRun.completedAt;
    const completedTime = new Date(completedAt._seconds * 1000);
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

    return completedTime < oneHourAgo;
  };

  // Helper function to get tooltip text for refresh button
  const getRefreshButtonTooltip = () => {
    if (!currentMonthData?.jobStatus?.lastRun?.completedAt) {
      return 'No previous job data available';
    }

    const completedAt = currentMonthData.jobStatus.lastRun.completedAt;
    const completedTime = new Date(completedAt._seconds * 1000);
    const timeDiff = Date.now() - completedTime.getTime();
    const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutesDiff = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (hoursDiff > 0) {
      return `Last refresh was ${hoursDiff} hour${
        hoursDiff > 1 ? 's' : ''
      } ago`;
    } else {
      return `Last refresh was ${minutesDiff} minute${
        minutesDiff > 1 ? 's' : ''
      } ago. Please wait at least 1 hour between refreshes.`;
    }
  };

  const fetchPartnerLists = useCallback(() => {
    axios
      .get<PartnerListsResponse>(`${getBaseUrl()}/api/partners/lists/urls`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then((res) => {
        setPartnerLists(res.data);
      })
      .catch(() => {});
  }, [token]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const fetchedSubscriptions =
        await stripeService.getCustomerSubscriptions(token);
      setSubscriptions(fetchedSubscriptions[0] || null);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  }, [token]);

  // Helper function to get active partners count
  const getActivePartnersCount = () => {
    if (!subscriptions) return 0;
    return subscriptions.activePartners || 0;
  };

  useEffect(() => {
    // Track page view
    FirebaseAnalyticsService.trackNavigation('Home', '/admin/home');

    fetchPartnerLists();
    fetchCurrentMonthAnalytics();
    fetchSubscriptions();
  }, [fetchPartnerLists, fetchCurrentMonthAnalytics, fetchSubscriptions]);

  // Helper function to handle external navigation
  const handleExternalNavigation = (url?: string | null) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const openPlaybook = (playbookName: string, url?: string | null) => {
    FirebaseAnalyticsService.trackPlaybookInteraction(
      playbookName,
      'View cocoOpps',
      { url },
    );
    handleExternalNavigation(url);
  };

  // Helper function to handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    FirebaseAnalyticsService.trackEngagement('notification_click', 1, {
      notification_id: notification.id,
      notification_title: notification.title,
      was_read: notification.read,
    });

    if (!notification.read) {
      await markAsRead(notification.id);
    }
    // You can add navigation logic here based on notification type
  };

  const onboardingStepsDone = getCurrentOnboardingStepNumber();
  const onboardingPercentComplete = Math.round((onboardingStepsDone / 5) * 100);

  return (
    <Box w="100%" minH="100vh" mt="100px" px={{ base: 2, md: 8 }} py={4}>
      {/* Welcome Section */}
      <Text fontSize="2xl" fontWeight="700" mb={1}>
        Welcome back, {user?.displayName.split(' ')[0]}
      </Text>
      <Text color="gray.500" mb={6}>
        Here&apos;s where to focus today to grow your partner deals.
      </Text>
      {subscriptions && !subscriptions.hasConnectedPortal && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          <AlertTitle color="#9A5C00">Portal not connected</AlertTitle>
          <AlertDescription color="#9A5C00">
            Some features will be unavailable until you connect your portal.{' '}
            <NavLink
              to="/admin/settings"
              style={{
                color: '#9A5C00',
                fontWeight: '600',
                textDecoration: 'underline',
              }}
            >
              Connect here
            </NavLink>
            .
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics & Go to Ecosystem */}
      <Card p={8} borderRadius="30px" mb={8}>
        <Flex justify="space-between" align="flex-start">
          <div>
            <Text fontSize="2xl" fontWeight="700" mb={1}>
              Your Ecosystem Metrics
            </Text>
            <Text color="gray.500" mb={6}>
              Understand your partner landscape and unlock potential revenue
              streams.
            </Text>
          </div>
          <HStack spacing={3}>
            <Tooltip
              label={getRefreshButtonTooltip()}
              isDisabled={isAnalyticsJobOldEnough()}
              placement="top"
            >
              <Button
                bg="brand.400"
                borderRadius="full"
                fontWeight="500"
                alignSelf="flex-start"
                disabled={
                  !subscriptions?.hasConnectedPortal ||
                  !isAnalyticsJobOldEnough()
                }
                h="34px"
                _hover={{ bg: 'brand.300' }}
                onClick={() => {
                  FirebaseAnalyticsService.trackButtonClick(
                    'Refresh Data',
                    'Home Page',
                    {
                      section: 'ecosystem_metrics',
                    },
                  );
                  triggerAnalytics();
                }}
                isLoading={isLoading}
                leftIcon={<FaSync />}
              >
                Refresh Data
              </Button>
            </Tooltip>
            <NavLink to="/admin/ecosystem" style={{ textDecoration: 'none' }}>
              <Button
                bg="brand.400"
                borderRadius="full"
                fontWeight="500"
                alignSelf="flex-start"
                h="34px"
                _hover={{ bg: 'brand.300' }}
                onClick={() => {
                  FirebaseAnalyticsService.trackButtonClick(
                    'Go to Ecosystem',
                    'Home Page',
                    {
                      section: 'ecosystem_metrics',
                    },
                  );
                }}
              >
                Go to Ecosystem
              </Button>
            </NavLink>
          </HStack>
        </Flex>
        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <AlertTitle>Error loading analytics!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Flex mb={8} gap={6} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} flex={1}>
            <Box bg="white" borderRadius="xl" p={6} boxShadow="md">
              <Text fontWeight="500" mb={2} fontSize="sm" color="gray.400">
                Opportunity Value (cocoOpps)
              </Text>
              <HStack align="center" justify="space-between">
                {isLoading ? (
                  <Spinner size="md" color="brand.400" />
                ) : (
                  <>
                    <Text fontSize="2xl" fontWeight="bold">
                      {currentMonthData
                        ? formatCurrency(getOpportunityValue(currentMonthData))
                        : '$0'}
                    </Text>
                    <Box
                      height="35px"
                      width="35px"
                      bg="brand.50"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon as={FaDollarSign} color="green.400" />
                    </Box>
                  </>
                )}
              </HStack>
              <Text color="green.500" fontSize="sm" mt={2}>
                {currentMonthData
                  ? `${getOpportunityCount(currentMonthData)} opportunity deals`
                  : 'No data available'}
              </Text>
            </Box>
            {/* TODO: Removes cocoWon metrics. We need to figure out a better way to calculate this. */}
            {/* <Box bg="white" borderRadius="xl" p={6} boxShadow="md">
              <Text fontWeight="500" mb={2} fontSize="sm" color="gray.400">
                Confirmed Wins (cocoWon)
              </Text>
              <HStack align="center" justify="space-between">
                {isLoading ? (
                  <Spinner size="md" color="brand.400" />
                ) : (
                  <>
                    <Text fontSize="2xl" fontWeight="bold">
                      {currentMonthData
                        ? formatCurrency(getClosedWonAmount(currentMonthData))
                        : '$0'}
                    </Text>
                    <Box
                      height="35px"
                      width="35px"
                      bg="brand.50"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box
                        position="relative"
                        width="28px"
                        height="28px"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box
                          as="span"
                          position="absolute"
                          left="0"
                          width="18px"
                          height="18px"
                          borderRadius="full"
                          border="2px solid"
                          borderColor="green.400"
                          bg="transparent"
                          opacity={0.7}
                        />
                        <Box
                          as="span"
                          position="absolute"
                          right="0"
                          width="18px"
                          height="18px"
                          borderRadius="full"
                          border="2px solid"
                          borderColor="green.400"
                          bg="transparent"
                          opacity={0.7}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </HStack>
              <Text color="green.500" fontSize="sm" mt={2}>
                {currentMonthData
                  ? `${getClosedWonCount(currentMonthData)} closed won deals`
                  : 'No data available'}
              </Text>
            </Box> */}
            <Box bg="white" borderRadius="xl" p={6} boxShadow="md">
              <Text fontWeight="500" mb={2} fontSize="sm" color="gray.400">
                Active Partners
              </Text>
              <HStack align="center" justify="space-between">
                {isLoading ? (
                  <Spinner size="md" color="brand.400" />
                ) : (
                  <>
                    <Text fontSize="2xl" fontWeight="bold">
                      {getActivePartnersCount()}
                    </Text>
                    <Box
                      height="35px"
                      width="35px"
                      bg="brand.50"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon as={FaUser} color="green.400" />
                    </Box>
                  </>
                )}
              </HStack>
            </Box>
          </SimpleGrid>
        </Flex>
      </Card>

      {/* Notifications & Tasks */}
      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
        gap={6}
        alignItems="stretch"
        w="100%"
      >
        {/* Notifications */}
        <Box
          bg="white"
          borderRadius="xl"
          p={6}
          boxShadow="md"
          display="flex"
          flexDirection="column"
          minH={0}
          minW={0}
          h={{ base: 'auto', md: HOME_NOTIFICATIONS_GET_STARTED_HEIGHT }}
          alignSelf="stretch"
        >
          <Flex justify="space-between" align="center" mb={4} flexShrink={0}>
            <HStack>
              <Text fontWeight="700">Notifications</Text>
              {unreadCount > 0 && (
                <Box
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  minW="20px"
                  h="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {unreadCount}
                </Box>
              )}
            </HStack>
            <Text
              color="gray.400"
              fontSize="sm"
              cursor="pointer"
              onClick={() => {
                FirebaseAnalyticsService.trackEngagement(
                  'mark_all_notifications_read',
                  unreadCount,
                );
                markAllAsRead();
              }}
              _hover={{ color: 'gray.600' }}
            >
              Mark all read
            </Text>
          </Flex>
          {notificationsLoading ? (
            <Flex flex="1" minH={0} align="center" justify="center" py={4}>
              <Spinner size="md" color="brand.400" />
            </Flex>
          ) : notifications.length > 0 ? (
            <Box
              flex="1"
              minH={0}
              overflowY="auto"
              overflowX="hidden"
              w="100%"
              pr={1}
              sx={{
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <VStack align="stretch" spacing={3}>
                {notifications.map((notification) => (
                  <Box
                    key={notification.id}
                    bg={notification.read ? 'gray.50' : 'green.50'}
                    borderRadius="md"
                    p={3}
                    cursor="pointer"
                    opacity={notification.read ? 0.7 : 1}
                    _hover={{
                      bg: notification.read ? 'gray.100' : 'green.100',
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <Text fontWeight="600">{notification.title}</Text>
                    <Text color="gray.500" fontSize="sm" mt={1}>
                      {notification.message}
                    </Text>
                    <Text color="gray.500" fontSize="sm" mt={1}>
                      {formatAbsoluteTime(notification.createdAt)}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          ) : (
            <Flex flex="1" minH={0} align="center" justify="center">
              <Box
                bg="gray.50"
                borderRadius="md"
                p={4}
                textAlign="center"
                w="100%"
              >
                <Text color="gray.500" fontSize="sm">
                  No notifications yet
                </Text>
              </Box>
            </Flex>
          )}
        </Box>
        {/* Onboarding & Tasks Checklist */}
        <Box
          bg="white"
          borderRadius="xl"
          p={6}
          boxShadow="md"
          display="flex"
          flexDirection="column"
          minH={0}
          minW={0}
          h={{ base: 'auto', md: HOME_NOTIFICATIONS_GET_STARTED_HEIGHT }}
          alignSelf="stretch"
        >
          <Flex direction="column" mb={5} flexShrink={0} gap={2}>
            <Flex justify="space-between" align="flex-start" gap={3}>
              <Text fontWeight="700">Get Started</Text>
              <Text
                fontSize="sm"
                fontWeight="700"
                color="green.600"
                flexShrink={0}
              >
                {onboardingPercentComplete}%
              </Text>
            </Flex>
            <Text fontSize="sm" color="gray.500" lineHeight="tall">
              {onboardingStepsDone >= 5 ? (
                <>
                  5 of 5 completed — You&apos;ve finished the setup and unlocked
                  your full ecosystem.
                </>
              ) : (
                <>
                  {onboardingStepsDone} of 5 completed — finish setup to unlock
                  your full ecosystem.
                </>
              )}
            </Text>
            <Box
              position="relative"
              h="10px"
              borderRadius="full"
              bg="green.50"
              w="100%"
              overflow="hidden"
            >
              <motion.div
                initial={false}
                animate={{
                  width: `${Math.min(100, (onboardingStepsDone / 5) * 100)}%`,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 220,
                  damping: 28,
                  mass: 0.75,
                }}
                style={{
                  height: '100%',
                  borderRadius: 9999,
                  backgroundColor: onboardingProgressFill,
                }}
              />
            </Box>
          </Flex>

          <Box
            flex={1}
            minH={0}
            overflowY="auto"
            overflowX="hidden"
            w="100%"
            pr={1}
            sx={{
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <VStack align="stretch" spacing={0}>
            {/* Complete Company Profile */}
            <Accordion allowToggle border="none">
              <AccordionItem
                border="none"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <AccordionButton borderRadius="md" py={4}>
                  <Box
                    flex="1"
                    textAlign="left"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      as="span"
                      mr={3}
                      boxSize={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={
                        onboardingStatus.completeCompanyProfile
                          ? 'green.400'
                          : 'gray.300'
                      }
                      bg={
                        onboardingStatus.completeCompanyProfile
                          ? 'green.400'
                          : 'transparent'
                      }
                      position="relative"
                      cursor="pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleOnboardingToggle('completeCompanyProfile');
                      }}
                    >
                      {onboardingStatus.completeCompanyProfile && (
                        <Box
                          as="span"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Text
                      color={
                        onboardingStatus.completeCompanyProfile
                          ? 'gray.500'
                          : 'inherit'
                      }
                      textDecoration={
                        onboardingStatus.completeCompanyProfile
                          ? 'line-through'
                          : 'none'
                      }
                      fontWeight="600"
                    >
                      Complete your company profile
                    </Text>
                  </Box>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text color="gray.600" mb={4} fontSize="sm">
                    Add your company information, logo, and key details to
                    personalize your partner experience and improve match
                    quality.
                  </Text>
                  <NavLink
                    to="/admin/settings"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      bg="brand.400"
                      borderRadius="full"
                      fontWeight="500"
                      fontSize="14px"
                      h="35px"
                      _hover={{ bg: 'brand.300' }}
                    >
                      Complete Profile
                    </Button>
                  </NavLink>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Complete ICP */}
            <Accordion allowToggle border="none">
              <AccordionItem
                border="none"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <AccordionButton borderRadius="md" py={4}>
                  <Box
                    flex="1"
                    textAlign="left"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      as="span"
                      mr={3}
                      boxSize={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={
                        onboardingStatus.completeICP ? 'green.400' : 'gray.300'
                      }
                      bg={
                        onboardingStatus.completeICP
                          ? 'green.400'
                          : 'transparent'
                      }
                      position="relative"
                      cursor="pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleOnboardingToggle('completeICP');
                      }}
                    >
                      {onboardingStatus.completeICP && (
                        <Box
                          as="span"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Text
                      color={
                        onboardingStatus.completeICP ? 'gray.500' : 'inherit'
                      }
                      textDecoration={
                        onboardingStatus.completeICP ? 'line-through' : 'none'
                      }
                      fontWeight="600"
                    >
                      Complete your ICP
                    </Text>
                  </Box>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text color="gray.600" mb={4} fontSize="sm">
                    Define your Ideal Customer Profile to help us identify the
                    best partner opportunities and improve deal quality.
                  </Text>
                  <NavLink
                    to="/admin/settings"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      bg="brand.400"
                      borderRadius="full"
                      fontWeight="500"
                      fontSize="14px"
                      h="35px"
                      _hover={{ bg: 'brand.300' }}
                    >
                      Set Up ICP
                    </Button>
                  </NavLink>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Run Ecosystem Signals */}
            <Accordion allowToggle border="none">
              <AccordionItem
                border="none"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <AccordionButton borderRadius="md" py={4}>
                  <Box
                    flex="1"
                    textAlign="left"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      as="span"
                      mr={3}
                      boxSize={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={
                        onboardingStatus.runEcosystemSignals
                          ? 'green.400'
                          : 'gray.300'
                      }
                      bg={
                        onboardingStatus.runEcosystemSignals
                          ? 'green.400'
                          : 'transparent'
                      }
                      position="relative"
                      cursor="pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleOnboardingToggle('runEcosystemSignals');
                      }}
                    >
                      {onboardingStatus.runEcosystemSignals && (
                        <Box
                          as="span"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Text
                      color={
                        onboardingStatus.runEcosystemSignals
                          ? 'gray.500'
                          : 'inherit'
                      }
                      textDecoration={
                        onboardingStatus.runEcosystemSignals
                          ? 'line-through'
                          : 'none'
                      }
                      fontWeight="600"
                    >
                      Run Ecosystem Signals
                    </Text>
                  </Box>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text color="gray.600" mb={4} fontSize="sm">
                    Surface intent signals and co-selling opportunities with
                    partners aligned to your ICP and company profile.
                  </Text>
                  <NavLink
                    to="/admin/ecosystem-signals"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      bg="brand.400"
                      borderRadius="full"
                      fontWeight="500"
                      fontSize="14px"
                      h="35px"
                      _hover={{ bg: 'brand.300' }}
                    >
                      Open Ecosystem Signals
                    </Button>
                  </NavLink>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Connect your HubSpot Portal */}
            <Accordion allowToggle border="none">
              <AccordionItem
                border="none"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <AccordionButton borderRadius="md" py={4}>
                  <Box
                    flex="1"
                    textAlign="left"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      as="span"
                      mr={3}
                      boxSize={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={
                        onboardingStatus.connectHubSpot
                          ? 'green.400'
                          : 'gray.300'
                      }
                      bg={
                        onboardingStatus.connectHubSpot
                          ? 'green.400'
                          : 'transparent'
                      }
                      position="relative"
                      cursor="pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleOnboardingToggle('connectHubSpot');
                      }}
                    >
                      {onboardingStatus.connectHubSpot && (
                        <Box
                          as="span"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Text
                      color={
                        onboardingStatus.connectHubSpot
                          ? 'gray.500'
                          : 'inherit'
                      }
                      textDecoration={
                        onboardingStatus.connectHubSpot
                          ? 'line-through'
                          : 'none'
                      }
                      fontWeight="600"
                    >
                      Connect your HubSpot Portal
                    </Text>
                  </Box>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text color="gray.600" mb={4} fontSize="sm">
                    Connect your HubSpot portal to start tracking partner deals
                    and opportunities. This enables all analytics and playbook
                    features.
                  </Text>
                  <NavLink
                    to="/admin/settings"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      bg="brand.400"
                      borderRadius="full"
                      fontWeight="500"
                      fontSize="14px"
                      h="35px"
                      _hover={{ bg: 'brand.300' }}
                    >
                      Go to Settings
                    </Button>
                  </NavLink>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Invite a partner */}
            <Accordion allowToggle border="none">
              <AccordionItem border="none">
                <AccordionButton borderRadius="md" py={4}>
                  <Box
                    flex="1"
                    textAlign="left"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      as="span"
                      mr={3}
                      boxSize={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={
                        onboardingStatus.requestFirstPartner
                          ? 'green.400'
                          : 'gray.300'
                      }
                      bg={
                        onboardingStatus.requestFirstPartner
                          ? 'green.400'
                          : 'transparent'
                      }
                      position="relative"
                      cursor="pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleOnboardingToggle('requestFirstPartner');
                      }}
                    >
                      {onboardingStatus.requestFirstPartner && (
                        <Box
                          as="span"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Text
                      color={
                        onboardingStatus.requestFirstPartner
                          ? 'gray.500'
                          : 'inherit'
                      }
                      textDecoration={
                        onboardingStatus.requestFirstPartner
                          ? 'line-through'
                          : 'none'
                      }
                      fontWeight="600"
                    >
                      Invite a partner
                    </Text>
                  </Box>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text color="gray.600" mb={4} fontSize="sm">
                    Build your partner ecosystem by inviting a partner or
                    requesting a connection from the directory.
                  </Text>
                  <NavLink
                    to="/admin/ecosystem"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      bg="brand.400"
                      borderRadius="full"
                      fontWeight="500"
                      fontSize="14px"
                      h="35px"
                      _hover={{ bg: 'brand.300' }}
                    >
                      Go to Partners
                    </Button>
                  </NavLink>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            </VStack>
          </Box>
        </Box>
      </Grid>

      <Card p={8} borderRadius="30px" mb={8} mt={8}>
        <Flex justify="space-between" align="center" mb={6}>
          <Text fontSize="2xl" fontWeight="700">
            Ecosystem Playbooks
          </Text>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={4}>
          <EcosystemPlaybookCard
            title="Upsells & Expansion"
            description="Unlock revenue by expanding or upselling into new teams, departments, or geographies within your existing accounts."
            count={partnerLists.counts?.expansionUpsellPlay}
            url={partnerLists.urls?.expansionUpsellPlay}
            icon={HiOutlineChartBar}
            onView={() =>
              openPlaybook(
                'Upsells & Expansion',
                partnerLists.urls?.expansionUpsellPlay,
              )
            }
          />
          <EcosystemPlaybookCard
            title="Accelerate Opportunities"
            description="Leverage your partner and their existing knowledge and relationships to speed up the sales cycle."
            count={partnerLists.counts?.accelerateOps}
            url={partnerLists.urls?.accelerateOps}
            icon={MdOutlineRocketLaunch}
            onView={() =>
              openPlaybook(
                'Accelerate Opportunities',
                partnerLists.urls?.accelerateOps,
              )
            }
          />
          <EcosystemPlaybookCard
            title="Generate Opportunities"
            description="Prioritise high-intent leads who've demonstrated buying behaviour with your partner's solutions."
            count={partnerLists.counts?.findNewOps}
            url={partnerLists.urls?.findNewOps}
            icon={MdOutlineTrackChanges}
            onView={() =>
              openPlaybook(
                'Generate Opportunities',
                partnerLists.urls?.findNewOps,
              )
            }
          />
          <EcosystemPlaybookCard
            title="Build Partnerships"
            description="Strengthen your partnership through proactive support of their expansion, deal acceleration and opportunity generation efforts."
            count={partnerLists.counts?.buildPartnerships}
            url={partnerLists.urls?.buildPartnerships}
            icon={FaHandshake}
            onView={() =>
              openPlaybook(
                'Build Partnerships',
                partnerLists.urls?.buildPartnerships,
              )
            }
          />
        </SimpleGrid>
      </Card>
    </Box>
  );
};
